import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/app/AppLayout";
import useTier from "../hooks/useTier";
import api from "../lib/api";
import { TrendingDown, AlertTriangle, Lock, ArrowUpRight, Factory, Sparkles, RefreshCw } from "lucide-react";

const SEVERITY_STYLES = {
  alert: { border: "border-red-500/50", bg: "bg-red-500/10", text: "text-red-300", icon: "text-red-400" },
  warn: { border: "border-amber-500/50", bg: "bg-amber-500/10", text: "text-amber-300", icon: "text-amber-400" },
  info: { border: "border-blue-500/50", bg: "bg-blue-500/10", text: "text-blue-300", icon: "text-blue-400" },
};

export default function LostOpportunityPage() {
  const { t } = useTranslation();
  const { capabilities, loading: tierLoading } = useTier();
  const enabled = !!capabilities?.lost_opportunity;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [days, setDays] = useState(90);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/lost-opportunity?days=${days}`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || t("common.loadingError"));
    }
    setLoading(false);
  }, [enabled, days, t]);

  useEffect(() => { load(); }, [load]);

  if (tierLoading) return <AppLayout><div className="p-8 text-slate-500">{t("common.loading")}</div></AppLayout>;

  if (!enabled) {
    return (
      <AppLayout>
        <div className="px-8 py-8 max-w-4xl mx-auto" data-testid="lost-opp-locked">
          <div className="label-tech mb-2">{t("lostOpportunity.biLabel")}</div>
          <h1 className="font-display font-bold text-3xl text-slate-100 mb-3">{t("lostOpportunity.title")}</h1>

          <div className="tech-card p-10 mt-6 text-center">
            <div className="w-20 h-20 rounded-full bg-violet-500/10 border border-violet-500/40 flex items-center justify-center mx-auto mb-5">
              <Lock size={36} className="text-violet-400"/>
            </div>
            <div className="font-display font-bold text-2xl text-slate-100 mb-3">{t("lostOpportunity.unlockTitle")}</div>
            <p className="text-sm text-slate-400 max-w-md mx-auto mb-6">
              {t("lostOpportunity.unlockBody")}
            </p>
            <Link
              to="/billing"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium"
              data-testid="lost-opp-upgrade-link"
            >
              {t("billing.upgrade")} Pro+ · €99{t("landing.pricing.mo")} <ArrowUpRight size={14}/>
            </Link>
          </div>
        </div>
      </AppLayout>
    );
  }

  const totals = data?.totals || {};
  const insights = data?.insights || [];
  const missingOps = data?.missing_operations_breakdown || [];
  const marketGaps = data?.market_machine_gaps || [];
  const suggestions = data?.suggested_machines || [];

  return (
    <AppLayout>
      <div className="px-8 py-8 max-w-7xl mx-auto" data-testid="lost-opp-page">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="label-tech mb-2 text-violet-400">{t("lostOpportunity.biLabel")}</div>
            <h1 className="font-display font-bold text-3xl text-slate-100 flex items-center gap-2">
              <TrendingDown className="text-violet-400" size={28}/> {t("lostOpportunity.title")}
            </h1>
            <p className="text-sm text-slate-400 mt-2">
              {t("lostOpportunity.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-sm text-slate-100"
              data-testid="lost-opp-period"
            >
              <option value="30">{t("lostOpportunity.last30")}</option>
              <option value="90">{t("lostOpportunity.last90")}</option>
              <option value="180">{t("lostOpportunity.last180")}</option>
              <option value="365">{t("lostOpportunity.lastYear")}</option>
            </select>
            <button
              onClick={load}
              disabled={loading}
              className="px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm flex items-center gap-2 border border-slate-700 disabled:opacity-50"
              data-testid="lost-opp-reload"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""}/>
              {t("common.reload")}
            </button>
          </div>
        </div>

        {error && <div className="mb-4 px-4 py-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm" data-testid="lost-opp-error">{error}</div>}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Kpi
            label={t("lostOpportunity.externalized")}
            value={`€${(totals.missed_external_cost_eur || 0).toLocaleString("it-IT")}`}
            sub={t("lostOpportunity.quotesCount", { count: totals.quotes_with_missing_ops || 0 })}
            accent="red"
          />
          <Kpi
            label={t("lostOpportunity.marketRfqsLost")}
            value={totals.market_rfqs_lost || 0}
            sub={`€${(totals.market_rfqs_value_lost_eur || 0).toLocaleString("it-IT")} ${t("lostOpportunity.estValueSuffix")}`}
            accent="amber"
          />
          <Kpi
            label={t("lostOpportunity.ownRfqsWithOffers")}
            value={`${totals.own_rfqs_with_offers || 0} / ${totals.own_rfqs_total || 0}`}
            sub={t("lostOpportunity.responseRate")}
            accent="blue"
          />
          <Kpi
            label={t("lostOpportunity.period")}
            value={`${data?.period_days || days} ${t("lostOpportunity.daysUnit")}`}
            sub={t("lostOpportunity.realtime")}
            accent="violet"
          />
        </div>

        {/* AI Insights */}
        {insights.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display font-semibold text-lg text-slate-100 mb-3 flex items-center gap-2">
              <Sparkles size={16} className="text-amber-400"/> {t("lostOpportunity.insights")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="lost-opp-insights">
              {insights.map((ins, i) => {
                const sty = SEVERITY_STYLES[ins.severity] || SEVERITY_STYLES.info;
                return (
                  <div key={`insight-${ins.title}-${i}`} className={`p-4 rounded-md border ${sty.border} ${sty.bg}`} data-testid={`insight-${i}`}>
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={18} className={sty.icon + " mt-0.5 flex-shrink-0"}/>
                      <div>
                        <div className={`font-semibold text-sm ${sty.text}`}>{ins.title}</div>
                        <div className="text-xs text-slate-300 mt-1">{ins.body}</div>
                        {ins.value_eur > 0 && (
                          <div className="text-[10px] label-tech mt-2 text-slate-500">
                            {t("lostOpportunity.impact")} <span className={sty.text + " font-mono-data"}>€{ins.value_eur.toLocaleString("it-IT")}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Suggested Machines from catalog */}
        {suggestions.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display font-semibold text-lg text-slate-100 mb-3 flex items-center gap-2">
              <Factory size={16} className="text-emerald-400"/> {t("lostOpportunity.suggestedMachines")}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="lost-opp-suggestions">
              {suggestions.map((s, i) => (
                <div key={`suggestion-${s.machine_category || i}`} className="tech-card p-5 border border-emerald-500/30" data-testid={`suggestion-${i}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="label-tech text-emerald-400 mb-1">{t("lostOpportunity.category")}</div>
                      <div className="font-display font-semibold text-slate-100">{s.category_label}</div>
                    </div>
                    <div className="text-right">
                      <div className="label-tech text-slate-500">{t("lostOpportunity.potentialRecovery")}</div>
                      <div className="font-mono-data text-emerald-300 text-xl">€{(s.potential_revenue_eur || 0).toLocaleString("it-IT")}</div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mb-3">{s.rationale}</p>
                  <div className="space-y-2">
                    {(s.candidates || []).map((c, j) => (
                      <div key={c.id || `${s.machine_category}-${j}`} className="flex items-center justify-between gap-3 p-2 rounded-md bg-slate-900/50 border border-slate-800">
                        <div className="min-w-0">
                          <div className="text-sm text-slate-100 font-medium truncate">{c.brand} {c.model}</div>
                          <div className="text-[10px] text-slate-500 truncate">{c.description}</div>
                        </div>
                        <div className="text-right text-[10px] font-mono-data text-slate-400 flex-shrink-0">
                          <div>{c.axes} {t("lostOpportunity.axesUnit")} · {c.precision_um} µm</div>
                          <div className="text-amber-400">€{c.hourly_rate}/h</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Missing operations breakdown */}
        {missingOps.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display font-semibold text-lg text-slate-100 mb-3">{t("lostOpportunity.missingOps")}</h2>
            <div className="tech-card overflow-hidden">
              <table className="w-full text-sm" data-testid="lost-opp-missing-ops">
                <thead className="bg-slate-900/60">
                  <tr className="text-left label-tech text-slate-500">
                    <th className="px-4 py-2">{t("lostOpportunity.colOperation")}</th>
                    <th className="px-4 py-2">{t("lostOpportunity.colQuotes")}</th>
                    <th className="px-4 py-2 text-right">{t("lostOpportunity.colOutsourceCost")}</th>
                  </tr>
                </thead>
                <tbody>
                  {missingOps.map((mo) => (
                    <tr key={mo.operation} className="border-t border-slate-800">
                      <td className="px-4 py-2 text-slate-100">{mo.operation}</td>
                      <td className="px-4 py-2 text-slate-400 font-mono-data">{mo.count}</td>
                      <td className="px-4 py-2 text-right text-red-300 font-mono-data">€{(mo.estimated_cost_eur || 0).toLocaleString("it-IT")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Market gaps */}
        {marketGaps.length > 0 && (
          <section className="mb-8">
            <h2 className="font-display font-semibold text-lg text-slate-100 mb-3">{t("lostOpportunity.marketGaps")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="lost-opp-market-gaps">
              {marketGaps.map((g) => (
                <div key={g.machine_category || g.label} className="tech-card p-4">
                  <div className="label-tech text-amber-400 mb-1">{g.label}</div>
                  <div className="font-mono-data text-2xl text-slate-100 mb-1">€{(g.estimated_value_eur || 0).toLocaleString("it-IT")}</div>
                  <div className="text-xs text-slate-400">{t("lostOpportunity.openRfqs", { count: g.rfq_count })}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {!loading && insights.length === 0 && missingOps.length === 0 && marketGaps.length === 0 && (
          <div className="tech-card p-10 text-center" data-testid="lost-opp-empty">
            <div className="text-slate-400 mb-2">{t("lostOpportunity.empty")}</div>
            <div className="text-xs text-slate-600">{t("lostOpportunity.emptyHint")}</div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function Kpi({ label, value, sub, accent }) {
  const colors = {
    red: "text-red-300",
    amber: "text-amber-300",
    blue: "text-blue-300",
    violet: "text-violet-300",
  };
  return (
    <div className="tech-card p-4">
      <div className="label-tech mb-2">{label}</div>
      <div className={`font-mono-data text-2xl ${colors[accent] || "text-slate-100"}`}>{value}</div>
      <div className="text-[10px] text-slate-500 mt-1">{sub}</div>
    </div>
  );
}
